import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Spinner,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Divider,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiMoreVertical, 
  FiEdit2, 
  FiTrash2, 
  FiPercent,
  FiUsers,
  FiUser,
  FiAlertTriangle,
  FiSearch
} from 'react-icons/fi';
import { useUserContext } from '../../../context/UserContext';
import { useAccessControl } from '../../../hooks/useAccessControl';
import {
  useGetPayrollDeductions,
  useCreatePayrollDeduction,
  useUpdatePayrollDeduction,
  useDeletePayrollDeduction,
  useNameEnquiry
} from '../../../services/payrollService';
import { useGetEmployees } from '../../../services/employeeService';
import { useGetPlanInfo } from '../../../services/planService';
import { formatCurrency } from '../../../utils/currency';

const PayrollDeductions = () => {
  const { user } = useUserContext();
  const { canManageDeductions } = useAccessControl();
  const toast = useToast();
  
  // State
  const [formData, setFormData] = useState({
    deduction_name: '',
    deduction_type: 'PERCENTAGE',
    percentage_rate: 0,
    flat_rate_amount: 0,
    is_company_wide: true,
    employee_id: '',
    // Settlement account fields
    settlement_account_number: '',
    settlement_bank_code: '',
    settlement_bank_name: '',
    settlement_account_name: '',
    settlement_currency: 'NGN'
  });
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [deductionToDelete, setDeductionToDelete] = useState(null);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const dropdownRef = useRef(null);
  
  // Settlement account state
  const [settlementBankSearchTerm, setSettlementBankSearchTerm] = useState("");
  const [showSettlementBankDropdown, setShowSettlementBankDropdown] = useState(false);
  const settlementBankDropdownRef = useRef(null);
  const [isVerifyingSettlementAccount, setIsVerifyingSettlementAccount] = useState(false);
  const [settlementAccountVerificationError, setSettlementAccountVerificationError] = useState(null);
  
  // Modals
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  // API hooks
  const { 
    data: payrollDeductions, 
    isLoading: payrollDeductionsIsLoading, 
    refetch: refetchPayrollDeductions 
  } = useGetPayrollDeductions();
  
  const { 
    getEmployeesData: employeesData, 
    getEmployeesIsLoading: employeesLoading, 
    getEmployeesError: employeesError 
  } = useGetEmployees({ enabled: true });
  
  // Get plan info for currency
  const { planInfo } = useGetPlanInfo();
  
  // Name enquiry hook for settlement accounts
  const nameEnquiryMutation = useNameEnquiry();
  
  // Nigerian banks list (for settlement accounts)
  const nigerianBanks = [
    { code: "044", name: "Access Bank" },
    { code: "058", name: "GTBank" },
    { code: "033", name: "UBA" },
    { code: "214", name: "First Bank" },
    { code: "011", name: "First Bank" },
    { code: "050", name: "Ecobank" },
    { code: "070", name: "Fidelity Bank" },
    { code: "221", name: "Stanbic IBTC" },
    { code: "068", name: "Standard Chartered" },
    { code: "232", name: "Sterling Bank" },
    { code: "032", name: "Union Bank" },
    { code: "215", name: "Unity Bank" },
    { code: "035", name: "Wema Bank" },
    { code: "057", name: "Zenith Bank" },
    { code: "076", name: "Polaris Bank" },
    { code: "082", name: "Keystone Bank" },
    { code: "090", name: "VFD Microfinance Bank" },
    { code: "100", name: "Suntrust Bank" },
    { code: "101", name: "Providus Bank" },
    { code: "102", name: "Titan Trust Bank" },
    { code: "103", name: "Globus Bank" },
    { code: "104", name: "Parallex Bank" },
    { code: "105", name: "Premium Trust Bank" },
    { code: "106", name: "Optimus Bank" },
    { code: "107", name: "Moniepoint Microfinance Bank" },
    { code: "108", name: "Opay" },
    { code: "109", name: "Kuda Bank" },
    { code: "110", name: "VBank" },
    { code: "111", name: "Mint Microfinance Bank" },
    { code: "112", name: "M42 Microfinance Bank" },
    { code: "113", name: "Carbon" },
    { code: "114", name: "Fairmoney Microfinance Bank" },
    { code: "115", name: "Sparkle Microfinance Bank" },
    { code: "116", name: "Kredi Money Microfinance Bank" },
    { code: "117", name: "9 Payment Service Bank" },
    { code: "118", name: "Airtel Smartcash PSB" },
    { code: "119", name: "MTN Momo PSB" },
    { code: "120", name: "PalmPay" }
  ];
  
  // Filter banks based on search term
  const filteredSettlementBanks = nigerianBanks.filter(bank =>
    bank.name?.toLowerCase().includes(settlementBankSearchTerm.toLowerCase()) ||
    bank.code?.toLowerCase().includes(settlementBankSearchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
      if (settlementBankDropdownRef.current && !settlementBankDropdownRef.current.contains(event.target)) {
        setShowSettlementBankDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Process employee data
  const employees = employeesData?.employees || [];
  
  // Process deductions data - similar to bank details processing
  const deductions = payrollDeductions?.data?.data || payrollDeductions?.data || payrollDeductions || [];
  
  // Filter employees based on search term
  const filteredEmployees = employees.filter(employee =>
    employee.first_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.last_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.employee_id?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );

  // Helper functions
  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setFormData(prev => ({
      ...prev,
      employee_id: employee.employee_id
    }));
    setEmployeeSearchTerm(`${employee.first_name} ${employee.last_name} (${employee.employee_id})`);
    setShowEmployeeDropdown(false);
  };

  const handleScopeChange = (isCompanyWide) => {
    setFormData(prev => ({
      ...prev,
      is_company_wide: isCompanyWide,
      employee_id: isCompanyWide ? '' : prev.employee_id
    }));
    if (isCompanyWide) {
      setSelectedEmployee(null);
      setEmployeeSearchTerm("");
    }
  };
  
  // Settlement account handlers
  const handleSettlementBankSelect = (bank) => {
    setFormData(prev => ({
      ...prev,
      settlement_bank_code: bank.code,
      settlement_bank_name: bank.name,
      settlement_account_name: "" // Clear account name when bank changes
    }));
    setSettlementBankSearchTerm(`${bank.name} (${bank.code})`);
    setShowSettlementBankDropdown(false);
    setSettlementAccountVerificationError(null);
    
    // Trigger name enquiry if account number is already filled
    if (formData.settlement_account_number && formData.settlement_account_number.length >= 10) {
      triggerSettlementNameEnquiry(bank.code, formData.settlement_account_number);
    }
  };
  
  // Auto-trigger name enquiry when both settlement bank_code and account_number are available
  useEffect(() => {
    if (
      formData.settlement_bank_code &&
      formData.settlement_account_number &&
      formData.settlement_account_number.length >= 10
    ) {
      // Debounce the API call
      const timeoutId = setTimeout(() => {
        triggerSettlementNameEnquiry(formData.settlement_bank_code, formData.settlement_account_number);
      }, 1000); // Wait 1 second after user stops typing
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.settlement_bank_code, formData.settlement_account_number]);
  
  const triggerSettlementNameEnquiry = async (bankCode, accountNumber) => {
    if (!bankCode || !accountNumber || accountNumber.length < 10) {
      return;
    }
    
    setIsVerifyingSettlementAccount(true);
    setSettlementAccountVerificationError(null);
    
    try {
      const result = await nameEnquiryMutation.mutateAsync({
        account_number: accountNumber,
        bank_code: bankCode
      });
      
      if (result.success && result.account_details?.verified) {
        setFormData(prev => ({
          ...prev,
          settlement_account_name: result.account_details.account_name || "",
          settlement_bank_name: result.account_details.bank_name || prev.settlement_bank_name
        }));
        setSettlementAccountVerificationError(null);
      } else {
        setSettlementAccountVerificationError(result.message || "Account verification failed");
        setFormData(prev => ({
          ...prev,
          settlement_account_name: ""
        }));
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          "Failed to verify account. Please check the account number and try again.";
      setSettlementAccountVerificationError(errorMessage);
      setFormData(prev => ({
        ...prev,
        settlement_account_name: ""
      }));
    } finally {
      setIsVerifyingSettlementAccount(false);
    }
  };
  
  const { createDeductionPayload: createDeductionMutation, createDeductionIsLoading: isCreating } = useCreatePayrollDeduction(() => {
    onCreateClose();
    setFormData({
      deduction_name: '',
      deduction_type: 'PERCENTAGE',
      percentage_rate: 0,
      flat_rate_amount: 0,
      is_company_wide: true,
      employee_id: '',
      settlement_account_number: '',
      settlement_bank_code: '',
      settlement_bank_name: '',
      settlement_account_name: '',
      settlement_currency: 'NGN'
    });
    setSelectedEmployee(null);
    setEmployeeSearchTerm("");
    setSettlementBankSearchTerm("");
    setSettlementAccountVerificationError(null);
    setIsVerifyingSettlementAccount(false);
    refetchPayrollDeductions();
  });
  
  const { updateDeductionPayload: updateDeductionMutation, updateDeductionIsLoading: isUpdating } = useUpdatePayrollDeduction(() => {
    onCreateClose();
    setEditingDeduction(null);
    setFormData({
      deduction_name: '',
      deduction_type: 'PERCENTAGE',
      percentage_rate: 0,
      flat_rate_amount: 0,
      is_company_wide: true,
      employee_id: '',
      settlement_account_number: '',
      settlement_bank_code: '',
      settlement_bank_name: '',
      settlement_account_name: '',
      settlement_currency: 'NGN'
    });
    setSettlementBankSearchTerm("");
    setSettlementAccountVerificationError(null);
    setIsVerifyingSettlementAccount(false);
    refetchPayrollDeductions();
  });
  
  const { deleteDeductionPayload, deleteDeductionIsLoading: isDeleting } = useDeletePayrollDeduction(() => {
    onDeleteClose();
    setDeductionToDelete(null);
    refetchPayrollDeductions();
  });

  // Theme colors
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle create/update deduction
  const handleSubmitDeduction = () => {
    if (!formData.deduction_name) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a deduction name',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
        variant: 'solid',
      });
      return;
    }

    if (formData.deduction_type === 'PERCENTAGE' && formData.percentage_rate <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid percentage rate',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
        variant: 'solid',
      });
      return;
    }

    if (formData.deduction_type === 'FLAT_RATE' && formData.flat_rate_amount <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid flat rate amount',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
        variant: 'solid',
      });
      return;
    }

    if (!formData.is_company_wide && !formData.employee_id) {
      toast({
        title: 'Validation Error',
        description: 'Please select an employee for employee-specific deduction',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
        variant: 'solid',
      });
      return;
    }

    const payload = {
      deduction_name: formData.deduction_name,
      deduction_type: formData.deduction_type,
      percentage_rate: formData.deduction_type === 'PERCENTAGE' ? formData.percentage_rate : null,
      flat_rate_amount: formData.deduction_type === 'FLAT_RATE' ? formData.flat_rate_amount : null,
      is_company_wide: formData.is_company_wide,
      employee_id: formData.is_company_wide ? null : formData.employee_id,
      // Settlement account fields (only include if provided)
      ...(formData.settlement_account_number && {
        settlement_account_number: formData.settlement_account_number,
        settlement_bank_code: formData.settlement_bank_code,
        settlement_bank_name: formData.settlement_bank_name,
        settlement_account_name: formData.settlement_account_name,
        settlement_currency: formData.settlement_currency || 'NGN'
      })
    };

    if (editingDeduction) {
      updateDeductionMutation({
        deductionId: editingDeduction.id,
        payload: payload
      });
    } else {
      createDeductionMutation(payload);
    }
  };

  // Handle edit deduction
  const handleEditDeduction = (deduction) => {
    setEditingDeduction(deduction);
    setFormData({
      deduction_name: deduction.deduction_name,
      deduction_type: deduction.deduction_type,
      percentage_rate: deduction.percentage_rate || 0,
      flat_rate_amount: deduction.flat_rate_amount || 0,
      is_company_wide: deduction.is_company_wide,
      employee_id: deduction.employee_id || '',
      settlement_account_number: deduction.settlement_account_number || '',
      settlement_bank_code: deduction.settlement_bank_code || '',
      settlement_bank_name: deduction.settlement_bank_name || '',
      settlement_account_name: deduction.settlement_account_name || '',
      settlement_currency: deduction.settlement_currency || 'NGN'
    });
    
    // Set settlement bank search term if bank details exist
    if (deduction.settlement_bank_name && deduction.settlement_bank_code) {
      setSettlementBankSearchTerm(`${deduction.settlement_bank_name} (${deduction.settlement_bank_code})`);
    } else {
      setSettlementBankSearchTerm("");
    }
    
    setSettlementAccountVerificationError(null);
    setIsVerifyingSettlementAccount(false);
    onCreateOpen();
  };

  // Handle delete deduction
  const handleDeleteDeduction = (deduction) => {
    setDeductionToDelete(deduction);
    onDeleteOpen();
  };

  // Confirm delete
  const confirmDelete = () => {
    if (deductionToDelete) {
      deleteDeductionPayload(deductionToDelete.id);
    }
  };

  // Get deduction type color
  const getDeductionTypeColor = (type) => {
    switch (type) {
      case 'PERCENTAGE':
        return 'blue';
      case 'FLAT_RATE':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Get scope color
  const getScopeColor = (isCompanyWide) => {
    return isCompanyWide ? 'purple' : 'orange';
  };

  if (payrollDeductionsIsLoading) {
    return (
      <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
        <Flex justify="center" align="center" h="200px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="lg" color="blue.600">
              Payroll Deductions
            </Heading>
            <Text color="gray.600">
              Manage company-wide and employee-specific payroll deductions
            </Text>
          </VStack>
          {canManageDeductions() && (
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={onCreateOpen}
            >
              Add Deduction
            </Button>
          )}
        </Flex>

        <Divider />

        {/* Deductions Table */}
        {deductions && Array.isArray(deductions) && deductions.length > 0 ? (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Deduction Name</Th>
                  <Th>Type</Th>
                  <Th>Rate/Amount</Th>
                  <Th>Scope</Th>
                  <Th>Employee</Th>
                  <Th>Settlement Account</Th>
                  <Th>Status</Th>
                  {canManageDeductions() && <Th>Actions</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {deductions.map((deduction) => (
                  <Tr key={deduction.id}>
                    <Td fontWeight="medium">{deduction.deduction_name}</Td>
                    <Td>
                      <Badge colorScheme={getDeductionTypeColor(deduction.deduction_type)}>
                        {deduction.deduction_type}
                      </Badge>
                    </Td>
                    <Td>
                      {deduction.deduction_type === 'PERCENTAGE' ? (
                        <HStack>
                          <FiPercent />
                          <Text>{deduction.percentage_rate}%</Text>
                        </HStack>
                      ) : (
                        <Text>{formatCurrency(deduction.flat_rate_amount || 0, planInfo?.country)}</Text>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme={getScopeColor(deduction.is_company_wide)}>
                        {deduction.is_company_wide ? (
                          <HStack spacing={1}>
                            <FiUsers />
                            <Text>Company-wide</Text>
                          </HStack>
                        ) : (
                          <HStack spacing={1}>
                            <FiUser />
                            <Text>Employee-specific</Text>
                          </HStack>
                        )}
                      </Badge>
                    </Td>
                    <Td>
                      {deduction.employee_name || '-'}
                    </Td>
                    <Td>
                      {deduction.has_settlement_account || deduction.settlement_account_number ? (
                        <VStack align="start" spacing={0}>
                          <Badge colorScheme="green" variant="subtle" fontSize="xs">
                            Configured
                          </Badge>
                          {deduction.settlement_account_name && (
                            <Text fontSize="xs" color="gray.600" mt={1}>
                              {deduction.settlement_account_name}
                            </Text>
                          )}
                          {deduction.settlement_bank_name && (
                            <Text fontSize="xs" color="gray.500">
                              {deduction.settlement_bank_name}
                            </Text>
                          )}
                        </VStack>
                      ) : (
                        <Badge colorScheme="gray" variant="subtle" fontSize="xs">
                          Not Configured
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme={deduction.is_active ? 'green' : 'red'}>
                        {deduction.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    {canManageDeductions() && (
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FiEdit2 />}
                              onClick={() => handleEditDeduction(deduction)}
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              icon={<FiTrash2 />}
                              color="red.500"
                              onClick={() => handleDeleteDeduction(deduction)}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Box textAlign="center" py={10}>
            <FiAlertTriangle size={48} color="gray" />
            <Text mt={4} color="gray.600">
              No payroll deductions found. Create your first deduction to get started.
            </Text>
          </Box>
        )}

        {/* Create/Edit Deduction Modal */}
        <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {editingDeduction ? 'Edit Deduction' : 'Add New Deduction'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Deduction Name</FormLabel>
                  <Input
                    value={formData.deduction_name}
                    onChange={(e) => handleFormChange('deduction_name', e.target.value)}
                    placeholder="e.g., HMO (Health Insurance)"
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Deduction Type</FormLabel>
                  <Select
                    value={formData.deduction_type}
                    onChange={(e) => handleFormChange('deduction_type', e.target.value)}
                  >
                    <option value="PERCENTAGE">Percentage</option>
                    <option value="FLAT_RATE">Flat Rate</option>
                  </Select>
                </FormControl>
                
                {formData.deduction_type === 'PERCENTAGE' ? (
                  <FormControl>
                    <FormLabel>Percentage Rate (%)</FormLabel>
                    <NumberInput
                      value={formData.percentage_rate}
                      onChange={(value) => handleFormChange('percentage_rate', parseFloat(value) || 0)}
                      min={0}
                      max={100}
                      precision={2}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                ) : (
                  <FormControl>
                    <FormLabel>Flat Rate Amount</FormLabel>
                    <NumberInput
                      value={formData.flat_rate_amount}
                      onChange={(value) => handleFormChange('flat_rate_amount', parseFloat(value) || 0)}
                      min={0}
                      precision={2}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                )}
                
                <FormControl>
                  <FormLabel>Scope</FormLabel>
                  <Select
                    value={formData.is_company_wide ? 'company' : 'employee'}
                    onChange={(e) => handleScopeChange(e.target.value === 'company')}
                  >
                    <option value="company">Company-wide</option>
                    <option value="employee">Employee-specific</option>
                  </Select>
                </FormControl>
                
                {!formData.is_company_wide && (
                  <FormControl>
                    <FormLabel>Select Employee</FormLabel>
                    <Box position="relative" ref={dropdownRef}>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <FiSearch color="gray.300" />
                        </InputLeftElement>
                        <Input
                          value={employeeSearchTerm}
                          onChange={(e) => {
                            setEmployeeSearchTerm(e.target.value);
                            setShowEmployeeDropdown(true);
                          }}
                          onFocus={() => setShowEmployeeDropdown(true)}
                          placeholder="Search employees by name or ID..."
                        />
                      </InputGroup>
                      
                      {showEmployeeDropdown && filteredEmployees.length > 0 && (
                        <Box
                          position="absolute"
                          top="100%"
                          left={0}
                          right={0}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          boxShadow="lg"
                          zIndex={1000}
                          maxHeight="200px"
                          overflowY="auto"
                        >
                          {filteredEmployees.slice(0, 10).map((employee) => (
                            <Box
                              key={employee.employee_id}
                              p={3}
                              cursor="pointer"
                              _hover={{ bg: "gray.50" }}
                              onClick={() => handleEmployeeSelect(employee)}
                              borderBottom="1px solid"
                              borderColor="gray.100"
                            >
                              <Text fontWeight="medium">{employee.first_name} {employee.last_name}</Text>
                              <Text fontSize="sm" color="gray.600">
                                {employee.employee_id}
                              </Text>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </FormControl>
                )}
                
                {/* Settlement Account Section */}
                <Divider />
                <Box w="full">
                  <Heading size="sm" mb={4} color="gray.700">
                    Settlement Account (Optional)
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    Configure bank account for automatic settlement payments when payroll batches are completed.
                  </Text>
                  
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel>Settlement Account Number</FormLabel>
                      <InputGroup>
                        <Input
                          value={formData.settlement_account_number}
                          onChange={(e) => {
                            setFormData(prev => ({ 
                              ...prev, 
                              settlement_account_number: e.target.value,
                              settlement_account_name: ""
                            }));
                            setSettlementAccountVerificationError(null);
                          }}
                          placeholder="Enter account number"
                          isDisabled={isVerifyingSettlementAccount}
                        />
                        {isVerifyingSettlementAccount && (
                          <InputRightElement>
                            <Spinner size="sm" color="blue.500" />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      {settlementAccountVerificationError && (
                        <Text fontSize="sm" color="red.500" mt={1}>
                          {settlementAccountVerificationError}
                        </Text>
                      )}
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>Settlement Bank</FormLabel>
                      <Box position="relative" ref={settlementBankDropdownRef}>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <FiSearch color="gray.300" />
                          </InputLeftElement>
                          <Input
                            value={settlementBankSearchTerm}
                            onChange={(e) => {
                              setSettlementBankSearchTerm(e.target.value);
                              setShowSettlementBankDropdown(true);
                            }}
                            onFocus={() => setShowSettlementBankDropdown(true)}
                            placeholder="Search banks by name or code..."
                          />
                        </InputGroup>
                        
                        {showSettlementBankDropdown && filteredSettlementBanks.length > 0 && (
                          <Box
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            boxShadow="lg"
                            zIndex={1000}
                            maxHeight="200px"
                            overflowY="auto"
                          >
                            {filteredSettlementBanks.slice(0, 15).map((bank) => (
                              <Box
                                key={bank.code}
                                p={3}
                                cursor="pointer"
                                _hover={{ bg: "gray.50" }}
                                onClick={() => handleSettlementBankSelect(bank)}
                                borderBottom="1px solid"
                                borderColor="gray.100"
                              >
                                <Text fontWeight="medium">{bank.name}</Text>
                                <Text fontSize="sm" color="gray.600">
                                  Code: {bank.code}
                                </Text>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>Settlement Account Name</FormLabel>
                      <Input
                        value={formData.settlement_account_name}
                        readOnly
                        isDisabled
                        placeholder="Account name will be verified automatically"
                        bg="gray.50"
                        _disabled={{
                          opacity: 1,
                          cursor: "not-allowed"
                        }}
                      />
                      {formData.settlement_account_name && (
                        <Text fontSize="xs" color="green.600" mt={1}>
                          ✓ Account verified successfully
                        </Text>
                      )}
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>Settlement Currency</FormLabel>
                      <Select
                        value={formData.settlement_currency}
                        onChange={(e) => handleFormChange('settlement_currency', e.target.value)}
                      >
                        <option value="NGN">NGN - Nigerian Naira</option>
                        <option value="USD">USD - US Dollar</option>
                        <option value="GHS">GHS - Ghanaian Cedi</option>
                        <option value="KES">KES - Kenyan Shilling</option>
                        <option value="ZAR">ZAR - South African Rand</option>
                      </Select>
                    </FormControl>
                  </VStack>
                </Box>
                
                <HStack spacing={4} w="full">
                  <Button
                    colorScheme="blue"
                    onClick={handleSubmitDeduction}
                    isLoading={isCreating || isUpdating}
                    w="full"
                  >
                    {editingDeduction ? 'Update Deduction' : 'Create Deduction'}
                  </Button>
                  <Button onClick={onCreateClose} w="full">
                    Cancel
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Deduction</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <AlertTitle>Are you sure?</AlertTitle>
                  <AlertDescription>
                    This will permanently delete the deduction "{deductionToDelete?.deduction_name}". 
                    This action cannot be undone.
                  </AlertDescription>
                </Box>
              </Alert>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                isLoading={isDeleting}
              >
                Delete
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default PayrollDeductions;